import React, { useEffect, useState } from 'react';
import { ArrowDown } from 'lucide-react';
import { Button } from '../ui/button';
import { heroContent, companyInfo } from '../../data/mock';

const Hero = ({ onCtaClick }) => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-amber-50/30 via-white to-amber-50/20 z-0">
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
      }} />
      
      {/* Yellow/Amber decorative blur elements with parallax */}
      <div 
        className="absolute top-10 left-0 w-80 h-80 bg-amber-200/25 rounded-full blur-3xl transition-transform duration-100"
        style={{ transform: `translateY(${scrollY * 0.15}px)` }}
      />
      <div 
        className="absolute top-1/3 -left-20 w-96 h-96 bg-yellow-200/30 rounded-full blur-3xl transition-transform duration-100"
        style={{ transform: `translateY(${scrollY * 0.2}px)` }}
      />
      <div 
        className="absolute bottom-20 left-10 w-64 h-64 bg-amber-100/30 rounded-full blur-3xl transition-transform duration-100"
        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
      />
      
      <div 
        className="absolute top-20 right-0 w-72 h-72 bg-yellow-200/25 rounded-full blur-3xl transition-transform duration-100"
        style={{ transform: `translateY(${scrollY * 0.18}px)` }}
      />
      <div 
        className="absolute bottom-10 right-10 w-80 h-80 bg-amber-100/25 rounded-full blur-3xl transition-transform duration-100"
        style={{ transform: `translateY(${scrollY * 0.12}px)` }}
      />
      <div 
        className="absolute top-1/2 -right-10 w-64 h-64 bg-amber-300/20 rounded-full blur-3xl transition-transform duration-100"
        style={{ transform: `translateY(${scrollY * 0.22}px)` }}
      />
      
      {/* Center subtle glow with parallax */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-amber-50/30 rounded-full blur-3xl"
        style={{ transform: `translate(-50%, calc(-50% + ${scrollY * 0.08}px))` }}
      />
      
      {/* Decorative lines with parallax */}
      <div 
        className="absolute left-8 top-1/4 bottom-1/4 w-px bg-gradient-to-b from-transparent via-amber-400/30 to-transparent hidden lg:block"
        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
      />
      <div 
        className="absolute right-8 top-1/3 bottom-1/3 w-px bg-gradient-to-b from-transparent via-amber-400/30 to-transparent hidden lg:block"
        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
      />

      {/* Main content with slower parallax (moves up slower than scroll) */}
      <div 
        className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20"
        style={{ transform: `translateY(${scrollY * 0.3}px)`, opacity: Math.max(0, 1 - scrollY / 600) }}
      >
        <div className="text-center">
          {/* Brand tagline */}
          <p className="text-slate-500 text-xs sm:text-sm tracking-[0.15em] sm:tracking-[0.3em] uppercase mb-8 animate-fade-in px-4">
            {companyInfo.taglineEn}
          </p>

          {/* Main headline */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-slate-800 leading-tight mb-8 tracking-wide px-4">
            {heroContent.headline.split('\n').map((line, index) => (
              <span key={index} className="block">
                {line}
              </span>
            ))}
          </h1>

          {/* Subheadline */}
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-slate-600 max-w-4xl mx-auto mb-12 font-light leading-relaxed px-4">
            {heroContent.subheadline}
          </p>

          {/* CTA Button */}
          <div className="flex flex-col items-center gap-4">
            <Button
              onClick={onCtaClick}
              size="lg"
              className="bg-stone-800 hover:bg-stone-700 text-white px-10 py-6 text-base font-normal tracking-wide transition-all button-hover hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
            >
              {heroContent.ctaText}
            </Button>
            <span className="text-xs text-slate-500 tracking-wide">
              {heroContent.ctaTextEn}
            </span>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-6">
          <div className="flex flex-col items-center gap-2 animate-bounce">
            <span className="text-xs text-slate-400 tracking-widest">SCROLL</span>
            <ArrowDown className="w-4 h-4 text-slate-400" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
